import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import { mergeAriaIds } from "../../utils/aria";
import { devWarn } from "../../utils/devWarning";
import { isRenderableNode } from "../../utils/reactNode";
// 상수 모듈만 — TruncatedText 컴포넌트/host를 끌어오지 않는다(결합·순환 회피).
import { TRUNCATE_ATTR } from "../TruncatedText/truncation";
import {
  resolveFloatingPopoverPosition,
  type FloatingPopoverPlacement,
  type PopoverPlacement,
} from "../foundations/popover";
import { tooltipBubbleSurfaceCss } from "./tooltipBubble";
// 모듈 직접 경로 — index 경유 시 Modal.tsx를 끌어와 결합/순환 위험 (AnchoredPopup과 동일).
import { topmostModalEl, topmostModalMountNode } from "../Modal/modalStack";
import { prefersReducedMotion } from "../../utils/prefersReducedMotion";
import { isTooltipBubbleBlockingPointerTarget } from "./tooltipPointerTarget";
import { useTooltipController, type TooltipController } from "./useTooltipController";

export type TooltipPlacement = PopoverPlacement;

export interface TooltipProps extends Omit<HTMLAttributes<HTMLSpanElement>, "content"> {
  /** falsy content disables the wrapper; `0` is still rendered. */
  content: ReactNode;
  placement?: TooltipPlacement;
  /** `false` keeps the tooltip visual-only and does not attach aria-describedby. */
  describeTrigger?: boolean;
  /**
   * 외부 show/hide controller(`useTooltipController`). 넘기면 **manual mode** — Tooltip이 hover/focus로
   * self-trigger하지 않고 `controller.visible`로만 표시되며, 트리거 바인딩은 consumer가
   * `controller.triggerProps`로 직접 건다. 위치 보정·portal·Escape dismiss·aria는 그대로 Tooltip이 담당.
   * 생략하면 내부 controller로 기존 auto mode(hover/focus 자동).
   */
  controller?: TooltipController;
  children: ReactNode;
}

// 4-way placement → resolveFloatingPopoverPosition의 centered placement.
const PLACEMENT_TO_FLOATING: Record<TooltipPlacement, FloatingPopoverPlacement> = {
  top: "top",
  bottom: "bottom",
  left: "left",
  right: "right",
};

// resting gap 보존 — 기존 inline tooltip의 0.3rem(≈5px) margin과 동일.
const TOOLTIP_OFFSET = 5;
const TOOLTIP_MARGIN = 8;

// enter/exit opacity fade 길이(ms). 지각의 핵심은 duration이 아니라 easing 곡선(ease-in — 아래 transition)이라
// duration은 짧게 둔다. prefers-reduced-motion이면 fade 생략(즉시 표시/제거).
const FADE_IN_MS = 120;
const FADE_OUT_MS = 120;

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

// visible일 때만 portal로 떠 position:fixed로 viewport 기준 배치(overflow/transform 조상 탈출).
// hidden이면 inline display:none으로 트리에 남아 aria-describedby description을 상존시킨다.
// $visible=display(트리 존재), $active=opacity(fade). exit 중엔 $visible이면서 $active=false로 fade-out.
const Bubble = styled.span<{ $visible: boolean; $active: boolean }>`
  position: fixed;
  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  pointer-events: auto;
  ${tooltipBubbleSurfaceCss}

  /* enter/exit opacity fade. enter 시작점(opacity 0)은 portal 삽입 시 @starting-style이 제공 — JS flip 불필요(Modal 동형). */
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  /* 방향별 duration+easing. 표시는 ease-in — opacity 지각이 로그라 저-opacity 구간을 길게 끌어야 "서서히
     materialize"로 읽힌다(ease/ease-out은 초반에 확 보여 pop). 사라짐은 ease-out으로 빠르게 뺀다. */
  transition: opacity
    ${({ $active }) => ($active ? `${FADE_IN_MS}ms ease-in` : `${FADE_OUT_MS}ms ease-out`)};
  @starting-style {
    opacity: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

type DescribedProps = { "aria-describedby"?: string };

/**
 * WAI-ARIA APG tooltip. bubble은 visible일 때 portal+position:fixed로 떠 overflow/transform 조상을 탈출.
 *
 * 용도: **저작한 설명**(content prop)을 트리거에 붙일 때. 잘린 텍스트의 전체값 복원은 이걸로 감싸지
 * 말고 `TruncatedText`/truncation 마커를 쓴다 — 같은 버블·타이밍·a11y로 뜨되 부착 모델이 다르다
 * (감싸면 이중 버블 + 래퍼가 flex 폭-제약을 끊어 truncation 자체가 깨진다). 한 요소엔 하나만.
 */
export const Tooltip = ({
  content,
  placement = "top",
  describeTrigger = true,
  controller,
  children,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}: TooltipProps) => {
  const id = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const [phase, setPhase] = useState<"hidden" | "shown" | "exiting">("hidden");
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // 내부 controller는 항상 만든다(조건부 훅 금지). controller prop이 오면 그걸 써서 외부가 가시성을
  // 구동하고(manual mode), 없으면 내부 controller로 hover/focus 자동 표시(auto mode).
  const internalController = useTooltipController();
  const vis = controller ?? internalController;
  const manual = controller != null;
  const { visible, show, hide, scheduleHide } = vis;
  // manual mode에선 consumer가 controller.triggerProps로 트리거를 직접 바인딩한다 — wrapper auto-bind 끔.
  const autoBind = manual ? null : internalController.triggerProps;

  // visible을 phase로 승격 — exit fade 동안 portal을 유지했다 MOTION_MS 뒤 unmount(Modal 동형).
  // reduced-motion이면 fade 없이 즉시 hidden. rendered=portal 존재, active=opacity 1(fade 타깃).
  useEffect(() => {
    if (visible) {
      setPhase("shown");
      return;
    }
    if (phaseRef.current === "hidden") return;
    if (prefersReducedMotion()) {
      setPhase("hidden");
      return;
    }
    setPhase("exiting");
    const timer = window.setTimeout(() => setPhase("hidden"), FADE_OUT_MS);
    return () => window.clearTimeout(timer);
  }, [visible]);

  const rendered = phase !== "hidden";
  const active = phase === "shown";

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      onKeyDown?.(event);
      // focus가 wrapper 안일 때의 Escape — 흡수(stopPropagation)해 상위(모달 등) 닫힘을 막는다(APG).
      // hover-shown(focus 밖) Escape는 아래 document 리스너가 흡수 없이 처리.
      if (event.key === "Escape" && visible) {
        event.stopPropagation();
        hide();
      }
    },
    [onKeyDown, visible, hide],
  );

  // portal mount: visible일 때 트리거가 topmost 모달 안이면 그 Dialog로(콘텐츠 위 + aria-modal 서브트리,
  // Dialog는 transform 없어 fixed 유지), 아니면 body로. tooltip은 모달 commit 이후(hover/focus)에 떠
  // AnchoredPopup의 rAF relocate가 불필요하다.
  useEffect(() => {
    if (!rendered) return;
    const owner = topmostModalEl();
    const inModal =
      owner != null && wrapperRef.current != null && owner.contains(wrapperRef.current);
    const mountNode = (inModal ? topmostModalMountNode() : null) ?? document.body;
    const el = document.createElement("div");
    el.setAttribute("data-c6-tooltip-root", "");
    mountNode.appendChild(el);
    setPortalEl(el);
    return () => {
      setPortalEl(null);
      el.remove();
    };
  }, [rendered]);

  // dev 가드: 잘림 마커가 달린 요소를 Tooltip으로 감싸면 hover 시 버블이 둘(설명+복원) 뜬다.
  // 두 부착 모델은 이름이 kinship을 암시하지 않아 런타임에서 잡는다 — 한 요소엔 하나만.
  useEffect(() => {
    if (wrapperRef.current?.querySelector(`[${TRUNCATE_ATTR}]`)) {
      devWarn(
        "[c6] <Tooltip> children contain a truncation marker (data-c6-truncate). " +
          "Both attach a bubble on hover — use Tooltip for an authored description OR the " +
          "truncation marker for clipped-text restore, not both on one element.",
      );
    }
  }, []);

  const updatePosition = useCallback(() => {
    const wrapper = wrapperRef.current;
    const bubble = bubbleRef.current;
    // 스크롤 중 트리거가 detach될 수 있다 — 끊긴 rect로 잘못 배치하지 않는다.
    if (!wrapper?.isConnected || !bubble) return;
    const bubbleRect = bubble.getBoundingClientRect();
    const next = resolveFloatingPopoverPosition({
      anchorRect: wrapper.getBoundingClientRect(),
      popoverSize: { width: bubbleRect.width, height: bubbleRect.height },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      placement: PLACEMENT_TO_FLOATING[placement],
      offset: TOOLTIP_OFFSET,
      margin: TOOLTIP_MARGIN,
    });
    setPosition({ left: next.left, top: next.top });
  }, [placement]);

  // Tooltip은 현재 포인터/focus 대상을 보조하는 일시 힌트다. capture scroll이 시작되면 anchor를
  // 따라 재배치하지 않고 즉시 닫아 새로 드러나는 목록 행을 가리지 않는다. phase도 곧바로 hidden으로
  // 내려 exit fade 동안 stale 위치에 남는 꼬리를 만들지 않는다. resize는 아래에서 계속 재배치한다.
  const dismissOnScroll = useCallback(() => {
    phaseRef.current = "hidden";
    setPhase("hidden");
    hide();
  }, [hide]);

  // pointer-events:auto hover bridge는 bubble을 읽을 수 있게 하지만, 조밀한 목록에서 bubble이 이웃
  // option을 덮으면 그 option의 pointer 진입까지 가로챈다. 실제 hit point 아래에 다른 Tooltip
  // trigger/interactive target이 있을 때만 fade tail 없이 즉시 치워 콘텐츠 탐색을 우선한다.
  const dismissIfBlockingPointerTarget = useCallback(
    (event: ReactMouseEvent<HTMLSpanElement>) => {
      const bubble = bubbleRef.current;
      const trigger = wrapperRef.current;
      if (
        !bubble ||
        !trigger ||
        !isTooltipBubbleBlockingPointerTarget({
          bubble,
          trigger,
          clientX: event.clientX,
          clientY: event.clientY,
        })
      ) {
        return false;
      }

      phaseRef.current = "hidden";
      setPhase("hidden");
      hide();
      return true;
    },
    [hide],
  );

  // paint 전 배치 — flash 없이 정위치에서 노출. content 변경 시 크기/위치 갱신.
  useLayoutEffect(() => {
    if (!visible || !portalEl) return;
    updatePosition();
  }, [visible, portalEl, updatePosition, content]);

  // portal 준비 전 visible 전이부터 exit fade가 끝날 때까지 scroll dismiss를 유지한다. visible만 구독하면
  // hide 직후 listener가 먼저 빠져 120ms fade tail이 scroll 위에 남는다.
  const scrollDismissActive = visible || rendered;
  useLayoutEffect(() => {
    if (!scrollDismissActive) return;
    window.addEventListener("scroll", dismissOnScroll, true);
    return () => window.removeEventListener("scroll", dismissOnScroll, true);
  }, [scrollDismissActive, dismissOnScroll]);

  useEffect(() => {
    if (!visible || !portalEl) return;
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [visible, portalEl, updatePosition]);

  // hover-shown(focus가 wrapper 밖) tooltip의 Escape dismiss — WCAG 1.4.13 dismissable.
  // 흡수 안 함: 상위 모달 등의 Escape를 가로채지 않는다. focus-shown은 wrapper handleKeyDown이
  // 먼저 stopPropagation해 native 이벤트가 document에 닿지 않으므로 여기서 중복 처리되지 않는다.
  useEffect(() => {
    if (!visible) return;
    const onDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") hide();
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, [visible, hide]);

  const hasContent = isRenderableNode(content);

  // content가 renderable→falsy로 바뀌면 아래 early-return이 wrapper/이벤트를 걷어내 hide 경로가 끊긴다.
  // 떠 있던 상태와 pending open을 controller에서 명시적으로 닫는다.
  useEffect(() => {
    if (!hasContent) hide();
  }, [hasContent, hide]);

  // Only host elements guarantee that aria-describedby reaches the DOM.
  const canCloneTrigger =
    isValidElement(children) && typeof (children as ReactElement).type === "string";

  // Composite/Fragment triggers need focus delegation because wrapper aria-describedby
  // does not describe the actual focused descendant.
  useEffect(() => {
    if (!describeTrigger || canCloneTrigger || !hasContent) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || target === wrapper) return;
      const existing = target.getAttribute("aria-describedby");
      const ids = existing ? existing.split(" ").filter(Boolean) : [];
      if (!ids.includes(id)) {
        target.setAttribute("aria-describedby", [...ids, id].join(" "));
      }
    };

    const onFocusOut = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || target === wrapper) return;
      const existing = target.getAttribute("aria-describedby");
      if (!existing) return;
      const ids = existing.split(" ").filter((s) => s && s !== id);
      if (ids.length > 0) {
        target.setAttribute("aria-describedby", ids.join(" "));
      } else {
        target.removeAttribute("aria-describedby");
      }
    };

    wrapper.addEventListener("focusin", onFocusIn);
    wrapper.addEventListener("focusout", onFocusOut);

    return () => {
      wrapper.removeEventListener("focusin", onFocusIn);
      wrapper.removeEventListener("focusout", onFocusOut);
      wrapper.querySelectorAll(`[aria-describedby~="${id}"]`).forEach((el) => {
        const existing = el.getAttribute("aria-describedby") ?? "";
        const ids = existing.split(" ").filter((s) => s && s !== id);
        if (ids.length > 0) {
          el.setAttribute("aria-describedby", ids.join(" "));
        } else {
          el.removeAttribute("aria-describedby");
        }
      });
    };
  }, [canCloneTrigger, describeTrigger, hasContent, id]);

  if (!hasContent) {
    return <>{children}</>;
  }

  let trigger: ReactNode = children;
  if (describeTrigger && canCloneTrigger) {
    const childProps = (children as ReactElement<DescribedProps>).props;
    trigger = cloneElement(children as ReactElement<DescribedProps>, {
      "aria-describedby": mergeAriaIds(childProps["aria-describedby"], id),
    });
  }

  const externalAriaDescribedBy = (rest as DescribedProps)["aria-describedby"];
  // cloneElement-able 트리거는 trigger 자신에 aria-describedby가 붙으므로 wrapper엔 외부 값만.
  // 그 외(text/fragment)는 focusin 위임으로 descendant에도 붙이고 wrapper에도 fallback id를 둔다.
  const includeWrapperId = describeTrigger && !canCloneTrigger;
  const wrapperAriaDescribedBy = mergeAriaIds(
    externalAriaDescribedBy,
    includeWrapperId ? id : undefined,
  );

  // portalEl 준비 전(rendered 첫 프레임)은 inline display:none으로 그려 잘못된 위치 flash를 막는다.
  const positioned = rendered && portalEl !== null;
  const bubble = (
    <Bubble
      ref={bubbleRef}
      id={id}
      role="tooltip"
      $visible={positioned}
      $active={active}
      style={positioned ? { left: position.left, top: position.top } : undefined}
      onMouseEnter={(event) => {
        if (!dismissIfBlockingPointerTarget(event)) show();
      }}
      onMouseMove={dismissIfBlockingPointerTarget}
      onMouseLeave={scheduleHide}
    >
      {content}
    </Bubble>
  );

  return (
    <Wrapper
      ref={wrapperRef}
      {...rest}
      data-c6-tooltip-trigger=""
      onMouseEnter={(e) => {
        onMouseEnter?.(e);
        autoBind?.onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        onMouseLeave?.(e);
        autoBind?.onMouseLeave(e);
      }}
      onFocus={(e) => {
        onFocus?.(e);
        // auto mode에서만 self-trigger(focus-visible 판정은 엔진의 triggerProps가 소유).
        autoBind?.onFocus(e);
      }}
      onBlur={(e) => {
        onBlur?.(e);
        autoBind?.onBlur();
      }}
      onKeyDown={handleKeyDown}
      aria-describedby={wrapperAriaDescribedBy}
    >
      {trigger}
      {positioned ? createPortal(bubble, portalEl) : bubble}
    </Wrapper>
  );
};
